// src/pages/DebtStudio/components/ScaleBuilder/ScaleChart.jsx
import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";

const renderLegend = (series) => (
  <ul
    className="flex gap-4 text-xs" // 👈 Tailwind text-xs = ~12px
    style={{ color: "hsl(var(--foreground))" }}
  >
    {series.map((s) => (
      <li key={s.key} className="flex items-center gap-1">
        <span
          style={{
            display: "inline-block",
            width: 20,
            height: 3,
            backgroundColor: s.color,
          }}
        />
        {s.label}
      </li>
    ))}
  </ul>
);

export default function ScaleChart({ rows }) {
  const chartData = rows.map((r) => ({
    x: r.date || r.tenor,
    interpolated: r.benchmark == null ? null : r.benchmark,
    yield: r.yield == null ? null : r.yield,
  }));

  const vals = [];
  chartData.forEach((row) => {
    if (typeof row.interpolated === "number") vals.push(row.interpolated);
    if (typeof row.yield === "number") vals.push(row.yield);
  });

  let domain = [0, 10];
  let ticks = [0, 2, 4, 6, 8, 10];
  if (vals.length) {
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const stepCandidates = [0.25, 0.5, 1, 2];
    let step = 1;
    let minR = Math.floor(min);
    let maxR = Math.ceil(max);
    for (const s of stepCandidates) {
      const minTest = Math.floor(min / s) * s;
      const maxTest = Math.ceil(max / s) * s;
      const count = Math.round((maxTest - minTest) / s) + 1;
      if (count >= 4 && count <= 6) {
        step = s;
        minR = minTest;
        maxR = maxTest;
        break;
      }
    }
    const arr = [];
    for (let v = minR; v <= maxR + 1e-9; v += step) {
      arr.push(+v.toFixed(2));
    }
    domain = [minR, maxR];
    ticks = arr;
  }

  const series = [
    { key: "interpolated", label: "Interpolated Curve (%)", color: "#0b6623" },
    { key: "yield", label: "Yield (%)", color: "#1f7bd8" },
  ];

  const tooltipFormatter = (value, name) => {
    if (value == null) return "—";
    return [`${parseFloat(value).toFixed(2)}%`, name];
  };

  return (
    <div className="h-48 mb-4">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ left: 8, right: 8, top: 8, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="x"
            tick={{ fontSize: 12 }} // 👈 X-axis font size
            label={{
              value: "Date",
              position: "insideBottom",
              offset: -5,
              style: { fontSize: 14 }, // 👈 X-axis label font
            }}
          />
          <YAxis
            domain={domain}
            ticks={ticks}
            tick={{ fontSize: 14 }} // 👈 Y-axis font size
            tickFormatter={(v) => `${v.toFixed(2)}%`}
          />
          <Tooltip
            formatter={tooltipFormatter}
            contentStyle={{
              backgroundColor: "hsl(var(--background))",
              border: "1px solid hsl(var(--border))",
              color: "hsl(var(--foreground))",
              borderRadius: 6,
              padding: "6px 8px",
              fontSize: 14, // 👈 Tooltip font
            }}
            labelStyle={{ color: "hsl(var(--foreground))", fontSize: 14 }}
          />
          <Legend content={() => renderLegend(series)} />
          <Line
            type="monotone"
            dataKey="interpolated"
            name="Interpolated Curve (%)"
            stroke="#0b6623"
            strokeWidth={3}
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="yield"
            name="Yield (%)"
            stroke="#1f7bd8"
            strokeWidth={3}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
